import styles from "./championship-card.module.css";
import Disclaimer from "components/functional/disclaimer";
import { useState, useEffect } from "react";
import instance from "services/api";
import ToggleActions from "components/functional/toggle-actions";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { permissionsService } from "services/permissions.service";
import {
  CalendarClock,
  Settings,
  UserPlus,
  Users,
  Swords,
  Crown,
} from "lucide-react";
import Spinner from "components/functional/spinner";

const ChampionshipCard = ({ team }) => {
  const [championship, setChampionship] = useState(null);
  const [captains, setCaptains] = useState([]);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const { championship_id, team_id } = team;

  const actionsLink = {
    manage_team: `/teams/${team_id}/management`,
    manage_preferences: `/teams/${team_id}/preferences`,
    invite_player: `/teams/${team_id}/invite`,
  };

  const teamActions = [
    {
      permission: "manage_team",
      label: t("teams.card.actions.manage_team"),
      icon: <Users size={16} className="icon icon-secondary" />,
      command: () => navigate(actionsLink.manage_team),
    },
    {
      permission: "manage_preferences",
      label: t("teams.card.actions.manage_preferences"),
      icon: <CalendarClock size={16} className="icon icon-secondary" />,
      command: () => navigate(actionsLink.manage_preferences),
    },
    {
      permission: "invite_player",
      label: t("teams.card.actions.invite_player"),
      icon: <UserPlus size={16} className="icon icon-secondary" />,
      command: () => navigate(actionsLink.invite_player),
    },
  ].filter((action) => {
    if (
      action.permission === "invite_player" &&
      permissionsService.hasPermission("manage_team", team_id)
    ) {
      // Admin can manage team already so no shortcut, only for players
      return false;
    }
    return permissionsService.hasPermission(action.permission, team_id);
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [championshipResponse, captainsResponse] = await Promise.all([
          instance.get(`/championships/${championship_id}`),
          instance.get(`/teams/${team_id}/players`),
        ]);
        setChampionship(championshipResponse.data);
        setCaptains(
          captainsResponse.data.filter((player) => player.is_captain)
        );
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [championship_id, team_id]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className={styles.content}>
      <header className={`${styles.header} mb-32`}>
        <div className={styles.icon}>
          <p className="title-3-variant-low">{team.name[0]}</p>
        </div>
        <h2 className={`${styles.title} title-3-variant-low`}>{team.name}</h2>
        <Disclaimer status="soccer" />
      </header>
      <section className={styles.data}>
        <div className={`${styles.item}`}>
          <h3 className={styles["grey-title"]}>
            {t("teams.championship-card.title")}
          </h3>
          <div className={styles.value}>
            <Swords size={16} className="icon icon-secondary" />
            <p className="title-3-variant-low">{championship?.name}</p>
          </div>
        </div>
        <div className={styles.item}>
          <h3 className={styles["grey-title"]}>
            {t("teams.championship-card.captains")}
          </h3>
          <div className={styles.value}>
            <Crown size={16} className="icon icon-secondary" />
            {captains.length === 0 ? (
              <p className="title-3-variant-low">
                {t("teams.championship-card.empty")}
              </p>
            ) : (
              <div className={styles.captains}>
                <p className="title-3-variant-low">
                  {captains.map(
                    (captain, index) =>
                      `${captain.first_name} ${captain.last_name}${
                        index < captains.length - 1 ? ", " : ""
                      }`
                  )}
                </p>
              </div>
            )}
          </div>
          <div className={styles.actions}>
            <ToggleActions
              actions={teamActions}
              btn={{
                class: "btn--secondary",
                icon: <Settings size={20} className="icon icon-secondary" />,
              }}
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChampionshipCard;
